import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useApp } from '../context/AppContext';
import { useLanguage } from '../i18n/LanguageContext';
import IntertownLocationPicker from '../components/IntertownLocationPicker';
import IntertownHero from '../components/IntertownHero';
import IntertownBookingView from '../components/IntertownBookingView';
import IntertownTripScreen from '../components/IntertownTripScreen';
import { createIntertownRequest, getIntertownQuote } from '../services/firebase';
import { forwardGeocode, getDirections } from '../services/maps';
import {
  ACTIONABLE_STEPS, customMaxDistanceKm, intertownBookingStep, intertownPickupLimit, intertownRebookPlan, quoteErrorField,
} from '../utils/intertownBooking';
import { intertownErrorKey } from '../utils/rideErrors';
import { forgetTown, isDestinationOutsideRoute, resolveTown } from '../utils/intertownTown';
import {
  flatFareRoutes, popularRouteFor, resolvePopularTowns, townPlace, townRadiusKm,
} from '../utils/intertownPopular';
import { QUOTE_TIMEOUT_MS, TOWN_LOOKUP_TIMEOUT_MS, isTimeoutError, withTimeout } from '../utils/intertownWait';

// A quote is valid only for the exact booking inputs that produced it. This
// protects the brief React re-render window after a passenger changes pickup,
// destination, route, or trip type. A fixed route's fare never depends on the
// destination pin (that town is resolved separately), so only custom trips
// include it: otherwise resolving the town would re-quote and trip the
// server's one-quote-per-800ms limit.
const placeKey = (place) => {
  const lat = Number(place?.lat);
  const lng = Number(place?.lng);
  return Number.isFinite(lat) && Number.isFinite(lng) ? `${lat.toFixed(6)},${lng.toFixed(6)}` : '';
};
const quoteInputKey = ({ isCustom, routeId, tripType, pickup, destination }) => (
  `${isCustom ? 'custom' : routeId}|${tripType}|${placeKey(pickup)}|${isCustom ? placeKey(destination) : ''}`
);

// A popular route's town centre on the map. Always asked through resolveTown, which remembers it.
const townGeocode = (route) => forwardGeocode(`${route.toName}, Andhra Pradesh, India`, {
  localContext: false,
  restrictToServiceArea: false,
});
// The admin's own town centre when the route has one (no billed lookup, and the very point the server
// measures from); otherwise the remembered map lookup above.
const lookupTown = (route) => {
  const own = townPlace(route);
  return own ? Promise.resolve(own) : resolveTown(route.toName, { geocode: () => townGeocode(route) });
};

export default function Intertown() {
  const { user, userProfile, platform, activeRide, activeIntertownRequest } = useApp();
  const { t } = useLanguage();
  const location = useLocation();
  const navigate = useNavigate();
  const config = platform?.intertown || null;
  const [tripType, setTripType] = useState('one_way');
  const [selectionMode, setSelectionMode] = useState('fixed');
  const [routeId, setRouteId] = useState('');
  const [pickup, setPickup] = useState(null);
  const [destination, setDestination] = useState(null);
  const [pickupNote, setPickupNote] = useState('');
  const [quote, setQuote] = useState(null);
  const [quoteError, setQuoteError] = useState(null);
  const [quoteRetry, setQuoteRetry] = useState(0);
  const [destinationRetry, setDestinationRetry] = useState(0);
  const [loadingQuote, setLoadingQuote] = useState(false);
  const [resolvingRouteDestination, setResolvingRouteDestination] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [locationPicker, setLocationPicker] = useState(null);
  // The rider's own drop spot inside the selected popular town (the fare stays the route's flat fare),
  // and whether a chosen drop is still being checked against the popular towns.
  const [exactDrop, setExactDrop] = useState(null);
  const [matchingDrop, setMatchingDrop] = useState(false);
  const dropToken = useRef(0);
  const dropApi = useRef({});

  const routes = useMemo(
    () => (Array.isArray(config?.routes) ? config.routes.filter((route) => route?.enabled !== false) : []),
    [config],
  );
  const customEnabled = config?.custom?.enabled === true;
  const isCustom = selectionMode === 'custom' && customEnabled;
  // routeId keeps the last fixed route while in custom mode, so a fixed
  // route's km/fare must never leak into the custom trip's display.
  const selectedRoute = isCustom ? null : (routes.find((route) => route.id === routeId) || null);
  const destinationForQuote = isCustom ? destination : null;
  // A spot of the rider's own inside a popular town needs custom map destinations to be on; without
  // them riders get the town itself. `dropPlace` is where the Anna is sent.
  const spot = !isCustom && customEnabled ? exactDrop : null;
  const dropPlace = isCustom ? destination : (spot || destination);
  const currentQuoteKey = quoteInputKey({ isCustom, routeId, tripType, pickup, destination: destinationForQuote });
  // The trip in progress, if any, comes from the app-wide listener: the same one that decides
  // whether the tab bar is shown, so this page and the bar can never disagree. A live trip takes
  // over the whole screen (IntertownTripScreen); booking is only shown when there is none.
  const activeRequest = activeIntertownRequest;

  useEffect(() => {
    if (!routeId && routes[0]?.id) setRouteId(routes[0].id);
    if (routeId && !routes.some((route) => route.id === routeId)) {
      setRouteId(routes[0]?.id || '');
      setExactDrop(null);
    }
  }, [routeId, routes]);

  // "Book this route again" on a trip receipt arrives with the old trip in navigation state.
  // Apply it once the booking settings are in, then clear it so a reload or Back never applies
  // it again (the same hand-over the local Book page uses). If that exact trip can no longer be
  // booked, say so and leave the page as it is. While a trip or ride is already in progress
  // nothing can be booked and the booking screen is not even shown, so the hand-over is only
  // cleared: applying it would cost a pointless fare lookup and pop up later, out of context.
  useEffect(() => {
    const rebook = location.state?.rebookIntertown;
    if (!rebook || !config) return;
    if (!activeRide && !activeIntertownRequest) {
      const plan = intertownRebookPlan(rebook, {
        routes, customEnabled, oneWayEnabled: config.oneWayEnabled, returnEnabled: config.returnEnabled,
      });
      if (plan) {
        setQuote(null);
        setQuoteError(null);
        setTripType(plan.tripType);
        if (plan.pickup) setPickup(plan.pickup);
        dropApi.current.cancelDropMatch();
        if (plan.mode === 'custom') {
          // A custom drop inside a popular town becomes that route with the rider's own spot.
          dropApi.current.chooseDrop(plan.destination, plan.tripType, 'custom');
        } else {
          setSelectionMode('fixed');
          setRouteId(plan.routeId);
          setExactDrop(plan.exactDrop || null);
        }
      } else {
        toast(t('intertown.rebookUnavailable'));
      }
    }
    navigate(location.pathname, { replace: true, state: {} });
  }, [location.state, location.pathname, config, routes, customEnabled, activeRide, activeIntertownRequest, navigate, t]);

  // A curated route represents a town, not an extra passenger task. Resolve
  // its town centre silently so fixed-route bookings retain a real destination
  // for navigation, the live map, and server-side arrival protection. The map
  // picker remains exclusively for a passenger choosing an unlisted town.
  useEffect(() => {
    if (isCustom || !selectedRoute?.toName) {
      // A lookup cancelled by switching to custom never reaches its own
      // finally, so clear the flag here or the booking bar stays stuck.
      setResolvingRouteDestination(false);
      return undefined;
    }
    let cancelled = false;
    setDestination(null);
    setResolvingRouteDestination(true);
    // A town's centre never moves: look it up once per device, not on every
    // tab open and route tap (each is a billed Geocoding call). The wait is
    // bounded: a lookup that never answers must end in "Try again", not a
    // button that stays on "Calculating…" for good.
    withTimeout(lookupTown(selectedRoute), TOWN_LOOKUP_TIMEOUT_MS, 'town')
      .then((place) => {
        if (!cancelled) setDestination(place);
      })
      .catch((error) => {
        if (!cancelled) {
          console.warn('Could not resolve fixed Intertown destination', error);
          const slow = isTimeoutError(error);
          // Drop the stalled lookup so "Try again" starts a fresh one instead of waiting on it.
          if (slow) forgetTown(selectedRoute.toName);
          toast.error(slow ? t('intertown.routeDestinationSlow') : t('intertown.routeDestinationFailed'));
        }
      })
      .finally(() => { if (!cancelled) setResolvingRouteDestination(false); });
    return () => { cancelled = true; };
  }, [isCustom, selectedRoute?.id, selectedRoute?.toName, t, destinationRetry]);

  useEffect(() => {
    setQuoteError(null);
    if (!config?.enabled || (!isCustom && (!routeId || !selectedRoute))) {
      setQuote(null);
      setLoadingQuote(false);
      return undefined;
    }
    // A custom drop that may turn out to be a popular town is not priced by distance until it is known.
    if (!pickup || (isCustom && !destinationForQuote) || matchingDrop) {
      setQuote(null);
      setLoadingQuote(false);
      return undefined;
    }

    let cancelled = false;
    setLoadingQuote(true);
    const inputKey = currentQuoteKey;
    const loadQuote = async () => {
      if (!isCustom) return getIntertownQuote({ routeId, tripType, pickup });
      const directions = await getDirections(pickup, destinationForQuote);
      return getIntertownQuote({
        routeId: 'custom', tripType, pickup, destination: destinationForQuote,
        distanceKm: directions.distanceKm, durationMin: directions.durationMin,
      });
    };
    // Bounded like the town lookup: a fare that never comes back becomes a visible
    // "Try again" instead of an endless "Calculating…".
    withTimeout(loadQuote(), QUOTE_TIMEOUT_MS, 'quote')
      .then((next) => { if (!cancelled) setQuote({ ...next, inputKey }); })
      .catch((error) => {
        if (!cancelled) {
          setQuote(null);
          // Shown in the booking bar, where it stays until the passenger acts.
          setQuoteError({
            message: isTimeoutError(error) ? t('intertown.quoteSlow') : (error?.message || t('intertown.quoteFailed')),
            field: quoteErrorField(error),
          });
        }
      })
      .finally(() => { if (!cancelled) setLoadingQuote(false); });
    return () => { cancelled = true; };
  }, [config?.enabled, currentQuoteKey, destinationForQuote, isCustom, matchingDrop, pickup, routeId, selectedRoute, t, tripType, quoteRetry]);

  // A chosen drop that is being checked against the popular towns stops waiting the moment the
  // rider changes anything else, and whatever the check finds is thrown away.
  const cancelDropMatch = () => {
    dropToken.current += 1;
    setMatchingDrop(false);
  };

  // Where the rider's chosen drop belongs. Inside a popular town (a route with a flat fare for this
  // trip type) it is that route's flat fare and the rider keeps their own spot; anywhere else it is a
  // distance trip, as before. `from` is where the drop was chosen: 'custom' (the map picker, or a
  // hand-over) or 'popular' (a spot in a popular town, when the trip type changes). A lookup that
  // fails or is slow just leaves the drop a distance trip.
  const chooseDrop = async (place, type, from = 'custom') => {
    const token = dropToken.current + 1;
    dropToken.current = token;
    const fromTown = selectedRoute?.toName || '';
    if (from === 'custom') {
      setQuote(null);
      setQuoteError(null);
      setSelectionMode('custom');
      setExactDrop(null);
      setDestination(place);
    }
    let route = null;
    if (flatFareRoutes(routes, type).length) {
      setMatchingDrop(true);
      try {
        const towns = await resolvePopularTowns(routes, type, {
          lookup: lookupTown,
          onTimeout: (candidate) => forgetTown(candidate.toName),
        });
        route = popularRouteFor({ place, routes, towns, tripType: type });
      } catch (error) {
        console.warn('Could not match the drop to a popular route', error);
      }
      if (dropToken.current !== token) return;
      setMatchingDrop(false);
    }
    setQuote(null);
    setQuoteError(null);
    // The fare lookup starts from the effect above. This answer arrives outside a tap, so the screen
    // could paint once before that effect runs: say "Calculating…" meanwhile, not a flash of "Try again".
    if (pickup) setLoadingQuote(true);
    if (route) {
      setSelectionMode('fixed');
      setRouteId(route.id);
      setExactDrop(place);
      if (from === 'custom') toast(t('intertown.switchedToPopular', { town: route.toName }));
    } else if (from === 'popular') {
      // The popular route has no flat fare for this trip type: the spot stays, priced by distance.
      setSelectionMode('custom');
      setExactDrop(null);
      setDestination(place);
      toast(t('intertown.switchedToDistance', { town: fromTown }));
    }
  };
  // The hand-over effect above runs from a closure of an older render: it reaches these through here.
  dropApi.current = { chooseDrop, cancelDropMatch };

  const chooseFixedRoute = (id) => {
    // Tapping the route that is already chosen changes nothing, and keeps the rider's spot in it.
    if (!isCustom && id === routeId) return;
    cancelDropMatch();
    setQuote(null);
    setQuoteError(null);
    setExactDrop(null);
    setSelectionMode('fixed');
    setRouteId(id);
  };

  const chooseCustom = () => {
    if (!isCustom) {
      cancelDropMatch();
      setQuote(null);
      setQuoteError(null);
      setSelectionMode('custom');
      setDestination(null);
      setExactDrop(null);
    }
    // Pickup first: the destination map measures its distance limit from it.
    setLocationPicker({ kind: pickup ? 'destination' : 'pickup' });
  };

  const changeTripType = (type) => {
    if (type === tripType) return;
    setQuote(null);
    setQuoteError(null);
    setTripType(type);
    cancelDropMatch();
    // A flat fare can exist for one trip type and not the other, so the same spot may belong to a
    // popular route for one and be a distance trip for the other.
    const chosenSpot = isCustom ? destination : spot;
    if (chosenSpot) chooseDrop(chosenSpot, type, isCustom ? 'custom' : 'popular');
  };

  const saveLocation = (place) => {
    if (locationPicker?.kind === 'pickup') {
      setQuote(null);
      setQuoteError(null);
      setPickup(place);
      setLocationPicker(isCustom && !destination ? { kind: 'destination' } : null);
      return;
    }
    if (locationPicker?.kind === 'destination') {
      setLocationPicker(null);
      chooseDrop(place, tripType, 'custom');
      return;
    }
    // A spot of the rider's own inside the chosen popular town: the fare does not change.
    if (locationPicker?.kind === 'town') setExactDrop(place);
    setLocationPicker(null);
  };

  const requestTrip = async () => {
    if (!pickup) { toast.error(t('intertown.pleaseChoosePickup')); return; }
    if (!dropPlace) { toast.error(t('intertown.pleaseChooseDestination')); return; }
    if (!quote || quote.inputKey !== currentQuoteKey || submitting || activeRequest || activeRide) {
      if (quote && quote.inputKey !== currentQuoteKey) {
        setQuote(null);
        setQuoteError(null);
        toast(t('intertown.calculatingFare'));
      }
      return;
    }
    setSubmitting(true);
    try {
      await createIntertownRequest({
        routeId: isCustom ? 'custom' : routeId,
        tripType,
        pickup,
        destination: dropPlace,
        // A rider's own spot inside a popular town: the server then judges arrival within 350 m.
        exactDrop: Boolean(spot),
        // The note is only a useful landmark. The selected pin, not free text,
        // is the authoritative pickup value sent to the assigned Anna.
        pickupNote,
        customerName: userProfile?.name || user?.displayName || '',
        distanceKm: isCustom ? quote?.route?.distanceKm : undefined,
        durationMin: isCustom ? quote?.route?.durationMin : undefined,
      });
      toast.success(t('intertown.requestSent'));
      setPickupNote('');
    } catch (error) {
      // If a remembered town location is what the server refused, drop it and
      // look the town up fresh so the next attempt is not stuck on a bad one.
      if (!isCustom && selectedRoute && !spot && isDestinationOutsideRoute(error)) {
        forgetTown(selectedRoute.toName);
        setDestinationRetry((n) => n + 1);
      }
      const key = intertownErrorKey(error);
      toast.error(key ? t(key) : t('intertown.requestFailed'));
    } finally {
      setSubmitting(false);
    }
  };

  // A trip already running is shown whether or not new bookings are open.
  if (activeRequest) return <IntertownTripScreen key={activeRequest.id} trip={activeRequest} />;

  if (!config?.enabled) {
    return (
      <div className="intertown-page">
        <section className="intertown-hero intertown-offline">
          <span className="intertown-kicker">{t('intertown.kicker')}</span>
          <h1>{t('intertown.offlineTitle')}</h1>
          <p>{t('intertown.offlineBody')}</p>
        </section>
      </div>
    );
  }

  const step = intertownBookingStep({
    hasActiveRide: Boolean(activeRide),
    submitting,
    isCustom,
    hasPickup: Boolean(pickup),
    hasDestination: Boolean(dropPlace),
    resolvingDestination: (resolvingRouteDestination && !spot) || matchingDrop,
    loadingQuote,
    hasQuote: Boolean(quote),
    errorField: quoteError?.field || null,
  });
  const stepLabel = {
    finish_ride: t('intertown.finishCurrentRide'),
    submitting: t('intertown.sendingRequest'),
    choose_destination: t('intertown.chooseDestinationCta'),
    choose_pickup: t('intertown.choosePickupCta'),
    // Short on purpose: a long label squeezes the fare beside it ("₹1,250 · One …").
    calculating: t('intertown.calculatingShort'),
    request: t('intertown.requestAnnaCta'),
    change_pickup: t('intertown.changePickupCta'),
    change_destination: t('intertown.changeDestinationCta'),
    retry: t('intertown.retry'),
  }[step];
  const pickupLimit = intertownPickupLimit(platform);
  const handleBookingAction = () => {
    if (step === 'choose_destination' || step === 'change_destination') setLocationPicker({ kind: 'destination' });
    else if (step === 'choose_pickup' || step === 'change_pickup') setLocationPicker({ kind: 'pickup' });
    else if (step === 'retry' && !dropPlace) setDestinationRetry((n) => n + 1);
    else if (step === 'retry') setQuoteRetry((n) => n + 1);
    else if (step === 'request') requestTrip();
  };

  return (
    <div className="intertown-page">
      <IntertownHero t={t} />

      <IntertownBookingView
        t={t}
        config={config}
        routes={routes}
        selectedRoute={selectedRoute}
        isCustom={isCustom}
        customEnabled={customEnabled}
        tripType={tripType}
        onTripType={changeTripType}
        pickup={pickup}
        destination={destination}
        pickupNote={pickupNote}
        onPickupNote={setPickupNote}
        quote={quote}
        quoteError={quoteError}
        loadingQuote={loadingQuote || matchingDrop}
        step={step}
        stepLabel={stepLabel}
        actionable={ACTIONABLE_STEPS.has(step)}
        onAction={handleBookingAction}
        pickupRadiusKm={pickupLimit?.radiusKm ?? null}
        onEditPickup={() => setLocationPicker({ kind: 'pickup' })}
        onChooseRoute={chooseFixedRoute}
        onChooseCustom={chooseCustom}
        exactDrop={spot}
        townReady={Boolean(destination)}
        onPickExactSpot={() => setLocationPicker({ kind: 'town' })}
        onClearExactSpot={() => setExactDrop(null)}
      />

      {locationPicker && (
        <IntertownLocationPicker
          // Remount between kinds: pickup chains straight into destination,
          // and the sheet must not carry the pickup's pin/state across.
          key={locationPicker.kind}
          // 'town' is a spot inside the chosen popular town: the destination map, measured from the
          // town's centre instead of from the pickup.
          kind={locationPicker.kind === 'town' ? 'destination' : locationPicker.kind}
          initialPlace={locationPicker.kind === 'pickup' ? pickup : locationPicker.kind === 'town' ? spot : destination}
          anchor={locationPicker.kind === 'town'
            ? destination
            : locationPicker.kind === 'destination' ? pickup : pickupLimit?.center || null}
          maxDistanceKm={locationPicker.kind === 'town'
            ? townRadiusKm(selectedRoute)
            : locationPicker.kind === 'destination'
              ? (isCustom ? customMaxDistanceKm(config.custom) : null)
              : pickupLimit?.radiusKm ?? null}
          townName={locationPicker.kind === 'town' ? selectedRoute?.toName : null}
          startAt={locationPicker.kind === 'town' ? destination : null}
          onConfirm={saveLocation}
          onClose={() => setLocationPicker(null)}
        />
      )}
    </div>
  );
}
